import {
  parsePhoneNumberFromString,
  isValidPhoneNumber,
} from "libphonenumber-js";

export const parseLocaleToCountry = (locale: string, language: string) => {
  try {
    const [, country] = locale.split("_");
    return new Intl.DisplayNames(language, { type: "region" }).of(country);
  } catch (_) {
    console.warn(`Country code not found in locale ${locale}`);
    return locale;
  }
};

/**
 * Replaces all dashes in a string with underscores
 * @example
 * replaceDashWithUnderscore("pl-PL") // "pl_PL"
 */
export const replaceDashWithUnderscore = (str: string) => {
  if (typeof str !== "string") return str;
  return str.replace(/-/g, "_");
};

/**
 * Concatenates dial code and phone number from an object
 * @example
 * concatDialCodeAndPhone({ name: "John", dialCode: "+48", phone: "123456789" }, ["dialCode", "phone"]) // "+48 123456789"
 */
export const concatDialCodeAndPhone = (
  object: Record<PropertyKey, any> | undefined,
  keys = ["dialCode", "phone"]
) => {
  if (!object) return "";
  const [dialCode, phone] = [object[keys[0]], object[keys[1]]];

  if (typeof dialCode !== "string" && typeof phone !== "string") return "";
  if (typeof dialCode !== "string") return phone;
  return `${dialCode} ${phone}`;
};

/**
 * Extracts dial code and national number from a given phone number
 * @example
 * // Returns { dialCode: "+48", phone: "777111222" }
 * extractPhoneDetails("48777111222", ["dialCode", "phone"])
 */
export function extractPhoneDetails<
  T extends string | undefined | null,
  U extends readonly [string, string] = readonly ["dialCode", "phone"],
>(
  phoneNumber: T,
  keys?: U
): U extends readonly [infer K1 extends string, infer K2 extends string]
  ? T extends string
    ? { [K in K1 | K2]: string }
    : Record<string, never>
  : never {
  const [dialCodeName, phoneName] = (keys ?? ["dialCode", "phone"]) as U;

  if (!phoneNumber) return {} as any;
  if (!isValidPhoneNumber(`+${phoneNumber}`)) return {} as any;
  const parsedNumber = parsePhoneNumberFromString(`+${phoneNumber}`);
  if (!parsedNumber) return {} as any;

  return {
    [dialCodeName]: `+${parsedNumber.countryCallingCode}`,
    [phoneName]: parsedNumber.nationalNumber,
  } as any;
}

export const transformObjectWithPhone = <
  T,
  U extends keyof T = keyof T,
  V extends readonly [string, string] = readonly ["phonePrefix", "phone"],
>(
  obj: T,
  phoneKey: U,
  keys?: V
) => {
  const { [phoneKey]: phone, ...rest } = (obj as T & { phone: string }) ?? {};

  if (typeof phone === "string") {
    return {
      ...rest,
      ...extractPhoneDetails(phone, keys),
    };
  }

  return obj;
};
